import { useState, useEffect } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { fetchUsageSummary, fetchUsageRecent } from '../../services/admin'
import type { AdminUsageSummary, AdminUsageRow } from '../../services/admin'
import StatCard from '../ui/StatCard'

export default function UsagePanel() {
  const [summary, setSummary] = useState<AdminUsageSummary | null>(null)
  const [recent, setRecent] = useState<AdminUsageRow[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)
  const PAGE_SIZE = 50

  useEffect(() => {
    loadData()
  }, [])

  async function loadData() {
    setLoading(true)
    try {
      const [s, r] = await Promise.all([
        fetchUsageSummary(),
        fetchUsageRecent(PAGE_SIZE, 0),
      ])
      setSummary(s)
      setRecent(r)
    } catch {
      // Error handled
    } finally {
      setLoading(false)
    }
  }

  async function loadPage(newPage: number) {
    try {
      const r = await fetchUsageRecent(PAGE_SIZE, newPage * PAGE_SIZE)
      setRecent(r)
      setPage(newPage)
    } catch {
      // Error handled
    }
  }

  if (loading) {
    return <p className="font-mono text-sm text-slate-500 animate-pulse">Loading usage...</p>
  }

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      {summary && (
        <>
          <div className="grid grid-cols-3 gap-4">
            <StatCard
              label="Total Cost (30d)"
              value={`$${summary.totals.total_cost.toFixed(2)}`}
              accent
            />
            <StatCard
              label="Total Tokens (30d)"
              value={summary.totals.total_tokens.toLocaleString()}
            />
            <StatCard
              label="API Calls (30d)"
              value={summary.totals.total_calls.toLocaleString()}
            />
          </div>

          {/* By model */}
          <div>
            <h3 className="font-mono text-[10px] tracking-widest uppercase text-slate-500 mb-3">By Model</h3>
            <div className="glass rounded-xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-cyan-500/10">
                    <th className="text-left px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Model</th>
                    <th className="text-right px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Calls</th>
                    <th className="text-right px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Tokens</th>
                    <th className="text-right px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.byModel.map(row => (
                    <tr key={row.model} className="border-b border-white/[0.06] last:border-0">
                      <td className="px-3 py-2 font-mono text-xs text-slate-200">{row.model}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-400 text-right tabular-nums">{row.calls.toLocaleString()}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-400 text-right tabular-nums">{row.tokens.toLocaleString()}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-400 text-right tabular-nums">${row.cost.toFixed(4)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Daily usage */}
          <div>
            <h3 className="font-mono text-[10px] tracking-widest uppercase text-slate-500 mb-3">Daily Usage (30d)</h3>
            {summary.byDay.length === 0 ? (
              <p className="font-mono text-xs text-slate-600">No usage data</p>
            ) : (
              <div className="glass rounded-xl p-4">
                {/* Simple bar chart */}
                <div className="flex items-end gap-px h-32">
                  {summary.byDay.map(day => {
                    const maxCost = Math.max(...summary.byDay.map(d => d.cost), 0.01)
                    const height = Math.max(2, (day.cost / maxCost) * 100)
                    return (
                      <div
                        key={day.day}
                        className="flex-1 group relative"
                        style={{ height: '100%', display: 'flex', alignItems: 'flex-end' }}
                      >
                        <div
                          className="w-full bg-cyan-500/30 hover:bg-cyan-500/50 transition-colors rounded-t-sm"
                          style={{ height: `${height}%` }}
                          title={`${day.day}: $${day.cost.toFixed(2)} | ${day.calls} calls`}
                        />
                      </div>
                    )
                  })}
                </div>
                <div className="flex justify-between mt-2">
                  <span className="font-mono text-[10px] text-slate-600">
                    {summary.byDay[0]?.day}
                  </span>
                  <span className="font-mono text-[10px] text-slate-600">
                    {summary.byDay[summary.byDay.length - 1]?.day}
                  </span>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Recent calls */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-mono text-[10px] tracking-widest uppercase text-slate-500">Recent Calls</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => loadPage(page - 1)}
              disabled={page === 0}
              className="p-1 text-slate-500 hover:text-cyan-400 transition-colors disabled:opacity-30"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="font-mono text-[10px] text-slate-500">Page {page + 1}</span>
            <button
              onClick={() => loadPage(page + 1)}
              disabled={recent.length < PAGE_SIZE}
              className="p-1 text-slate-500 hover:text-cyan-400 transition-colors disabled:opacity-30"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {recent.length === 0 ? (
          <p className="font-mono text-xs text-slate-600">No recent calls</p>
        ) : (
          <div className="glass rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-cyan-500/10">
                  <th className="text-left px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">User</th>
                  <th className="text-left px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Model</th>
                  <th className="text-right px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Tokens</th>
                  <th className="text-right px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Cost</th>
                  <th className="text-left px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Date</th>
                </tr>
              </thead>
              <tbody>
                {recent.map(row => (
                  <tr key={row.id} className="border-b border-white/[0.06] last:border-0">
                    <td className="px-3 py-2 font-mono text-xs text-slate-300">{row.email ?? row.user_id.slice(0, 8)}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-400">{row.model}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-400 text-right tabular-nums">{row.total_tokens.toLocaleString()}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-400 text-right tabular-nums">${parseFloat(row.cost_usd).toFixed(4)}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-500">{new Date(row.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
